import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { ArticlesRequest, ErsteMarketGoResource } from '../../resources';
import { ersteMarketAnalysisAtom, ErsteMarketAnalysisState } from '../../state';
import { Article } from '../../types';
import { Loadable } from '../../utils';
import { GetResourceHeader } from '../useResource';

export type UseErsteMarketAnalysisCommonParams = {
  ersteMarketGoResource: ErsteMarketGoResource;
  getResourceHeader: GetResourceHeader;
};

export type UseErsteMarketAnalysisCommonOutput = {
  articles: ErsteMarketAnalysisState;
  loadArticles: () => Promise<void>;
  loadMoreArticles: () => Promise<void>;
};

const PAGE_SIZE = 20;
const FIRST_PAGE = 1;

export const useErsteMarketAnalysisCommon = ({
  ersteMarketGoResource,
  getResourceHeader,
}: UseErsteMarketAnalysisCommonParams): UseErsteMarketAnalysisCommonOutput => {
  const [{ articles, hasMore, currentPage }, setState] = useAtom(ersteMarketAnalysisAtom);
  const isLoadingRef = useRef(false);

  const getArticles = useCallback(
    async (request?: ArticlesRequest): Promise<Article[]> => {
      const result = await ersteMarketGoResource.getArticles(request ?? {}, getResourceHeader());
      return result.data;
    },
    [ersteMarketGoResource, getResourceHeader],
  );

  const loadPage = useCallback(
    async (page: number): Promise<void> => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      setState(prev => ({
        ...prev,
        articles: Loadable.hasValue(prev.articles) ? Loadable.loadingWith(prev.articles) : Loadable.loading,
      }));

      try {
        const nextArticles = await getArticles({ page, limit: PAGE_SIZE });
        setState(prev => ({
          ...prev,
          articles: Loadable.loaded(
            page === FIRST_PAGE
              ? nextArticles
              : [...Loadable.getOrElse(prev.articles, [] as Article[]), ...nextArticles],
          ),
          currentPage: page,
          hasMore: nextArticles.length === PAGE_SIZE,
        }));
      } catch (e) {
        setState(prev => ({
          ...prev,
          articles: page === FIRST_PAGE ? Loadable.failed : prev.articles,
        }));
      } finally {
        isLoadingRef.current = false;
      }
    },
    [getArticles, setState],
  );

  const loadArticles = useCallback((): Promise<void> => loadPage(FIRST_PAGE), [loadPage]);

  const loadMoreArticles = useCallback((): Promise<void> => {
    if (!hasMore) return Promise.resolve();
    return loadPage(currentPage + 1);
  }, [hasMore, currentPage, loadPage]);

  return { articles, loadArticles, loadMoreArticles };
};
